import React, { useState } from 'react';
import { X, AlertTriangle, Loader } from 'lucide-react';
import { subscriptionService } from '../../services/subscriptionService';

const CancelSubscriptionModal = ({ isOpen, onClose, currentPlan, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cancelType, setCancelType] = useState('period_end'); // 'period_end' or 'immediate'
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const reasons = [
    'Too expensive',
    'Not using it enough',
    'Missing features I need',
    'Found a better alternative',
    'Business is closing',
    'Other'
  ];

  const handleConfirm = async () => {
    try {
      setLoading(true);
      setError(null);

      const cancelImmediately = cancelType === 'immediate';
      
      await subscriptionService.cancelSubscription(cancelImmediately, reason);
      
      if (onSuccess) {
        onSuccess(cancelImmediately);
      }
      
      onClose();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(err.response?.data?.error || 'Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        
        {/* Header */}
        <div className="bg-red-50 border-b border-red-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="bg-red-100 p-2 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Cancel Subscription</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          
          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Warning */}
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              We're sorry to see you go! Before you cancel, please let us know why you're leaving.
            </p>
            
            {/* Reason Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for cancelling (optional)
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              >
                <option value="">Select a reason...</option>
                {reasons.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Cancellation Options */}
          <div className="mb-6 space-y-3">
            <p className="text-sm font-medium text-gray-700 mb-2">When should we cancel?</p>
            
            {/* Cancel at Period End */}
            <label className="flex items-start p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 transition-colors">
              <input
                type="radio"
                name="cancelType"
                value="period_end"
                checked={cancelType === 'period_end'}
                onChange={(e) => setCancelType(e.target.value)}
                disabled={loading}
                className="mt-1 h-4 w-4 text-blue-600"
              />
              <div className="ml-3 flex-1">
                <p className="font-medium text-gray-900">At end of billing period</p>
                <p className="text-sm text-gray-600 mt-1">
                  Keep access until your current period ends. No further charges.
                </p>
              </div>
            </label>

            {/* Cancel Immediately */}
            <label className="flex items-start p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-red-300 transition-colors">
              <input
                type="radio"
                name="cancelType"
                value="immediate"
                checked={cancelType === 'immediate'}
                onChange={(e) => setCancelType(e.target.value)}
                disabled={loading}
                className="mt-1 h-4 w-4 text-red-600"
              />
              <div className="ml-3 flex-1">
                <p className="font-medium text-gray-900">Cancel immediately</p>
                <p className="text-sm text-gray-600 mt-1">
                  Lose access right away. No refund for remaining time.
                </p>
              </div>
            </label>
          </div>

          {/* What You'll Lose */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-semibold text-yellow-900 mb-2">
              ⚠️ You'll lose access to:
            </p>
            <ul className="text-sm text-yellow-800 space-y-1 ml-4 list-disc">
              <li>Customer management</li>
              <li>Job tracking and scheduling</li>
              <li>Invoicing and payments</li>
              <li>All your business data</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleConfirm}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader className="animate-spin mr-2 h-5 w-5" />
                  Cancelling...
                </>
              ) : (
                'Confirm Cancellation'
              )}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Keep Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelSubscriptionModal;